import React from 'react';
import { useResumeContext } from '../../context/ResumeContext';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Header Component
 * 
 * Provides navigation and progress tracking for the interactive resume.
 * Includes animated tab navigation and progress indicator.
 */
const Header = () => {
  const { activeSection, setActiveSection, progress } = useResumeContext();

  const sections = [
    { id: 'intro', title: 'Introduction', icon: '📚' },
    { id: 'framework', title: 'Framework', icon: '🔍' },
    { id: 'career', title: 'Career Journey', icon: '🚀' },
    { id: 'competency', title: 'Competency', icon: '📊' },
    { id: 'portfolio', title: 'Portfolio', icon: '💼' },
    { id: 'conclusion', title: 'Next Steps', icon: '✅' }
  ];

  // Handle section change with animation
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
  };

  return (
    <header className="sticky top-0 z-10 bg-white shadow-md p-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
          <motion.h1 
            className="text-2xl font-bold"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            The Learning Journey: Alexander Donoho
          </motion.h1>
          
          <motion.div 
            className="flex items-center gap-2 mt-2 sm:mt-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <span className="text-sm text-slate-600">Progress:</span>
            <div className="w-32 h-3 bg-slate-200 rounded-full overflow-hidden">
              <motion.div 
                className="h-3 bg-blue-500 rounded-full" 
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              ></motion.div>
            </div>
            <motion.span 
              className="text-sm font-medium"
              key={progress}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {progress}%
            </motion.span>
          </motion.div>
        </div>
        
        {/* Navigation tabs */}
        <nav className="flex gap-1 overflow-x-auto pb-2 md:pb-0">
          <AnimatePresence>
            {sections.map(section => (
              <motion.button
                key={section.id}
                onClick={() => handleSectionChange(section.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-t-lg transition-colors whitespace-nowrap ${
                  activeSection === section.id 
                    ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500 font-medium' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                whileHover={{ 
                  backgroundColor: activeSection === section.id ? '#EFF6FF' : '#F1F5F9',
                  y: -2
                }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.3, 
                  delay: 0.1 * sections.findIndex(s => s.id === section.id)
                }}
              >
                <span className="hidden md:inline">{section.icon}</span>
                <span>{section.title}</span>
                {activeSection === section.id && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500"
                    layoutId="activeSection"
                    transition={{ duration: 0.3 }}
                  />
                )}
              </motion.button>
            ))}
          </AnimatePresence>
        </nav>
      </div>
    </header>
  );
};

export default Header;